import {
  Button,
  Card,
  Field,
  Input,
  Select,
  Spinner,
  contrastRatio,
  deriveAccent,
  isValidHex,
  toneTokens,
  useToast,
  type NeutralTone,
} from '@aurora/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router';

import { api, ApiError } from '../api/client.js';
import type { MeResponse } from '../api/types.js';
import { brandingLogoUrl, brandingStore, type Branding } from '../state/branding.js';
import { tenantStore } from '../state/tenant.js';

interface Profile {
  smallBusiness: boolean;
  legalName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  zip: string | null;
  city: string | null;
  country: string | null;
  taxNumber: string | null;
  vatId: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  iban: string | null;
  bic: string | null;
  bankName: string | null;
}

type FormState = Record<Exclude<keyof Profile, 'smallBusiness'>, string> & {
  smallBusiness: boolean;
};

const EMPTY: FormState = {
  smallBusiness: false,
  legalName: '',
  addressLine1: '',
  addressLine2: '',
  zip: '',
  city: '',
  country: 'Deutschland',
  taxNumber: '',
  vatId: '',
  email: '',
  phone: '',
  website: '',
  iban: '',
  bic: '',
  bankName: '',
};

const FIELDS: Array<{
  key: Exclude<keyof Profile, 'smallBusiness'>;
  label: string;
  hint?: string;
}> = [
  {
    key: 'legalName',
    label: 'Firmenname (vollständig)',
    hint: 'Erscheint auf Rechnungen und Angeboten',
  },
  { key: 'addressLine1', label: 'Straße und Hausnummer' },
  { key: 'addressLine2', label: 'Adresszusatz' },
  { key: 'zip', label: 'PLZ' },
  { key: 'city', label: 'Ort' },
  { key: 'country', label: 'Land' },
  {
    key: 'taxNumber',
    label: 'Steuernummer',
    hint: '§14 UStG: Steuernummer ODER USt-IdNr. ist Pflicht',
  },
  { key: 'vatId', label: 'USt-IdNr.' },
  { key: 'email', label: 'E-Mail' },
  { key: 'phone', label: 'Telefon' },
  { key: 'website', label: 'Website' },
  { key: 'iban', label: 'IBAN' },
  { key: 'bic', label: 'BIC' },
  { key: 'bankName', label: 'Bank' },
];

const TONE_OPTIONS: Array<{ value: NeutralTone; label: string }> = [
  { value: 'warm', label: 'Warm (Beige, wie Aurora)' },
  { value: 'neutral', label: 'Neutral (reines Grau)' },
  { value: 'cool', label: 'Kühl (Blaugrau)' },
];

/**
 * Branding-Pflege (v6/E1): Name, Zeichen, Markenfarbe, Grundton — mit
 * Live-Vorschau der abgeleiteten Skala inkl. Kontrastwert. Beim Speichern
 * übernimmt die Shell das Branding sofort (brandingStore).
 */
function BrandingCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [brandName, setBrandName] = useState('');
  const [brandShort, setBrandShort] = useState('');
  const [brandMark, setBrandMark] = useState('');
  const [accentHex, setAccentHex] = useState('');
  const [neutralTone, setNeutralTone] = useState<NeutralTone>('warm');

  const branding = useQuery({
    queryKey: ['branding'],
    queryFn: () => api<{ branding: Branding }>('/api/v1/branding'),
  });

  useEffect(() => {
    const data = branding.data?.branding;
    if (data) {
      setBrandName(data.brandName ?? '');
      setBrandShort(data.brandShort ?? '');
      setBrandMark(data.brandMark ?? '');
      setAccentHex(data.accentHex ?? '');
      setNeutralTone(data.neutralTone);
    }
  }, [branding.data]);

  const save = useMutation({
    mutationFn: () =>
      api<{ branding: Branding }>('/api/v1/branding', {
        method: 'PUT',
        body: {
          brandName: brandName.trim() === '' ? null : brandName.trim(),
          brandShort: brandShort.trim() === '' ? null : brandShort.trim(),
          brandMark: brandMark.trim() === '' ? null : brandMark.trim(),
          accentHex: accentHex.trim() === '' ? null : accentHex.trim(),
          neutralTone,
        },
      }),
    onSuccess: (result) => {
      // Shell übernimmt sofort (Sidebar, Titel, Farben) — kein Reload nötig.
      brandingStore.set(result.branding);
      void queryClient.invalidateQueries({ queryKey: ['branding'] });
      toast.success('Branding gespeichert');
    },
    onError: (mutationError) =>
      toast.error(
        mutationError instanceof ApiError && mutationError.status === 403
          ? 'Dir fehlt die Berechtigung core.settings.manage.'
          : mutationError.message,
      ),
  });

  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      const buffer = await file.arrayBuffer();
      let binary = '';
      for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
      return api<{ branding: Branding }>('/api/v1/branding/logo', {
        method: 'POST',
        body: { mime: file.type, contentBase64: btoa(binary) },
      });
    },
    onSuccess: (result) => {
      brandingStore.set(result.branding);
      void queryClient.invalidateQueries({ queryKey: ['branding'] });
      toast.success('Logo hochgeladen');
    },
    onError: (mutationError) =>
      toast.error(
        mutationError instanceof ApiError && mutationError.status === 403
          ? 'Dir fehlt die Berechtigung core.settings.manage.'
          : mutationError.message,
      ),
  });

  const removeLogo = useMutation({
    mutationFn: () => api<{ branding: Branding }>('/api/v1/branding/logo', { method: 'DELETE' }),
    onSuccess: (result) => {
      brandingStore.set(result.branding);
      void queryClient.invalidateQueries({ queryKey: ['branding'] });
      toast.success('Logo entfernt');
    },
    onError: (mutationError) => toast.error(mutationError.message),
  });

  const currentLogoUrl = brandingLogoUrl(branding.data?.branding ?? null, tenantStore.get());

  /** Live-Vorschau der Ableitung — genau das, was die Shell setzen würde. */
  const preview = useMemo(() => {
    if (accentHex.trim() === '' || !isValidHex(accentHex.trim())) return null;
    const hex = accentHex.trim();
    return (['light', 'dark'] as const).map((mode) => {
      const scale = deriveAccent(hex, mode, neutralTone);
      const tones = toneTokens(neutralTone, mode);
      return {
        mode,
        scale,
        tones,
        ratio: contrastRatio(scale.accent, tones.surfaceActive),
      };
    });
  }, [accentHex, neutralTone]);

  return (
    <Card>
      <h2 style={{ fontSize: 'var(--au-text-lg)', marginBottom: 'var(--au-space-2)' }}>Branding</h2>
      <p className="page__lead" style={{ marginBottom: 'var(--au-space-4)' }}>
        Marke der Instanz: Name, Zeichen, EINE Markenfarbe und der Grundton der Flächen. Die
        vollständige Farbskala (hell/dunkel, Hover, Flächen) wird kontrastsicher abgeleitet.
      </p>
      <form
        style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--au-space-4)' }}
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Produktname" hint="Login-Seite, Fenstertitel — z. B. „Meier Werkzeuge ERP“">
          {(id) => (
            <Input id={id} value={brandName} onChange={(e) => setBrandName(e.target.value)} />
          )}
        </Field>
        <Field label="Kurzname" hint="Seitenleiste — z. B. „Meier“">
          {(id) => (
            <Input id={id} value={brandShort} onChange={(e) => setBrandShort(e.target.value)} />
          )}
        </Field>
        <Field label="Zeichen" hint="Fallback ohne Logo — z. B. ◈ oder M">
          {(id) => (
            <Input
              id={id}
              maxLength={8}
              value={brandMark}
              onChange={(e) => setBrandMark(e.target.value)}
            />
          )}
        </Field>
        <Field label="Grundton der Flächen">
          {(id) => (
            <Select
              id={id}
              value={neutralTone}
              onChange={(e) => setNeutralTone(e.target.value as NeutralTone)}
            >
              {TONE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label="Markenfarbe"
          hint="Eine Farbe genügt — leer lassen für den Standard-Akzent (Gold)."
        >
          {(id) => (
            <div style={{ display: 'flex', gap: 'var(--au-space-2)', alignItems: 'center' }}>
              <input
                type="color"
                aria-label="Markenfarbe wählen"
                value={isValidHex(accentHex.trim()) ? accentHex.trim() : '#a37f3f'}
                onChange={(e) => setAccentHex(e.target.value)}
                style={{
                  width: 42,
                  height: 34,
                  padding: 2,
                  border: '1px solid var(--au-border-strong)',
                  borderRadius: 'var(--au-radius-sm)',
                  background: 'var(--au-surface)',
                }}
              />
              <Input
                id={id}
                placeholder="#c8102e"
                value={accentHex}
                onChange={(e) => setAccentHex(e.target.value)}
              />
            </div>
          )}
        </Field>
        <Field label="Logo" hint="SVG, PNG oder JPEG, max. 2 MB — ersetzt das Zeichen.">
          {(id) => (
            <div style={{ display: 'flex', gap: 'var(--au-space-2)', alignItems: 'center' }}>
              {currentLogoUrl !== null ? (
                <img
                  src={currentLogoUrl}
                  alt="Aktuelles Logo"
                  style={{ maxHeight: 34, maxWidth: 120 }}
                />
              ) : null}
              <input
                id={id}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                style={{ fontSize: 'var(--au-text-sm)', flex: 1 }}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) uploadLogo.mutate(file);
                  event.target.value = '';
                }}
              />
              {currentLogoUrl !== null ? (
                <Button
                  type="button"
                  variant="subtle"
                  loading={removeLogo.isPending}
                  onClick={() => removeLogo.mutate()}
                >
                  Entfernen
                </Button>
              ) : null}
            </div>
          )}
        </Field>
        {preview !== null ? (
          <div style={{ display: 'flex', gap: 'var(--au-space-3)', alignItems: 'flex-end' }}>
            {preview.map(({ mode, scale, tones, ratio }) => (
              <div
                key={mode}
                style={{
                  flex: 1,
                  background: tones.bg,
                  border: `1px solid ${tones.border}`,
                  borderRadius: 'var(--au-radius-md)',
                  padding: 'var(--au-space-3)',
                  display: 'grid',
                  gap: 'var(--au-space-2)',
                }}
              >
                <span style={{ color: scale.accent, fontWeight: 600 }}>
                  {mode === 'light' ? 'Hell' : 'Dunkel'} · Kontrast {ratio.toFixed(2)} ✓
                </span>
                <span
                  style={{
                    background: scale.accent,
                    color: scale.accentContrast,
                    borderRadius: 'var(--au-radius-sm)',
                    padding: '4px 10px',
                    justifySelf: 'start',
                    fontSize: 'var(--au-text-sm)',
                  }}
                >
                  Button
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div />
        )}
        <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" loading={save.isPending}>
            Branding speichern
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** Einstellungen → Stammdaten: Briefkopf- und Pflichtangaben des Mandanten. */
export function TenantProfilePage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { me } = useOutletContext<{ me: MeResponse }>();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const activeSlug = tenantStore.get();
  const activeTenant = me.tenants.find((tenant) => tenant.slug === activeSlug) ?? me.tenants[0];
  const [tenantName, setTenantName] = useState(activeTenant?.name ?? '');

  const renameTenant = useMutation({
    mutationFn: () => api('/api/v1/tenant/name', { method: 'PUT', body: { name: tenantName } }),
    onSuccess: () => {
      toast.success('Mandantenname geändert');
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (mutationError) =>
      toast.error(
        mutationError instanceof ApiError && mutationError.status === 403
          ? 'Dir fehlt die Berechtigung core.settings.manage.'
          : mutationError.message,
      ),
  });

  const profile = useQuery({
    queryKey: ['tenant', 'profile'],
    queryFn: () => api<{ profile: Profile | null }>('/api/v1/tenant/profile'),
  });

  useEffect(() => {
    const data = profile.data?.profile;
    if (data) {
      setForm({
        ...(Object.fromEntries(
          Object.keys(EMPTY)
            .filter((key) => key !== 'smallBusiness')
            .map((key) => [key, data[key as keyof Profile] ?? '']),
        ) as Omit<FormState, 'smallBusiness'>),
        smallBusiness: data.smallBusiness,
      });
    }
  }, [profile.data]);

  const save = useMutation({
    mutationFn: () =>
      api('/api/v1/tenant/profile', {
        method: 'PUT',
        body: {
          ...Object.fromEntries(
            Object.entries(form)
              .filter(([key]) => key !== 'smallBusiness')
              .map(([key, value]) => [
                key,
                (value as string).trim() === '' ? null : (value as string).trim(),
              ]),
          ),
          smallBusiness: form.smallBusiness,
        },
      }),
    onSuccess: () => {
      setError(null);
      setFeedback('Stammdaten gespeichert — sie erscheinen ab jetzt auf allen Belegen.');
      void queryClient.invalidateQueries({ queryKey: ['tenant'] });
    },
    onError: (mutationError) => {
      setFeedback(null);
      setError(
        mutationError instanceof ApiError && mutationError.status === 403
          ? 'Dir fehlt die Berechtigung core.settings.manage.'
          : mutationError.message,
      );
    },
  });

  if (profile.isLoading) return <Spinner />;

  return (
    <div className="page">
      <h1 className="page__title">Stammdaten</h1>
      <p className="page__lead">
        Briefkopf- und Pflichtangaben (§14 UStG) — erscheinen auf Rechnungen, Angeboten und in
        E-Mail-Signaturen.
      </p>
      {error !== null ? <p className="page__error">{error}</p> : null}
      {feedback !== null ? <p style={{ color: 'var(--au-success)' }}>{feedback}</p> : null}

      <Card>
        <form
          style={{ display: 'flex', gap: 'var(--au-space-3)', alignItems: 'flex-end' }}
          onSubmit={(event) => {
            event.preventDefault();
            if (tenantName.trim() !== '') renameTenant.mutate();
          }}
        >
          <div style={{ flex: 1 }}>
            <Field
              label="Anzeigename des Mandanten"
              hint="Erscheint in der Kopfleiste und bei der Mandanten-Auswahl."
            >
              {(id) => (
                <Input
                  id={id}
                  required
                  value={tenantName}
                  onChange={(event) => setTenantName(event.target.value)}
                />
              )}
            </Field>
          </div>
          <Button type="submit" loading={renameTenant.isPending}>
            Umbenennen
          </Button>
        </form>
      </Card>

      <BrandingCard />

      <Card>
        <form
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'var(--au-space-4)',
          }}
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          {FIELDS.map((field) => (
            <Field
              key={field.key}
              label={field.label}
              {...(field.hint !== undefined ? { hint: field.hint } : {})}
            >
              {(id) => (
                <Input
                  id={id}
                  value={form[field.key]}
                  onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}
                />
              )}
            </Field>
          ))}
          <div
            style={{
              gridColumn: 'span 2',
              display: 'flex',
              gap: 'var(--au-space-2)',
              alignItems: 'center',
            }}
          >
            <input
              id="small-business"
              type="checkbox"
              checked={form.smallBusiness}
              onChange={(event) => setForm({ ...form, smallBusiness: event.target.checked })}
            />
            <label htmlFor="small-business" style={{ fontSize: 'var(--au-text-sm)' }}>
              Kleinunternehmer (§ 19 UStG) — keine Umsatzsteuer auf Belegen, Pflichthinweis wird
              automatisch ergänzt
            </label>
          </div>
          <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="submit" loading={save.isPending}>
              Speichern
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
